import React, { useState, useEffect } from 'react'
import {
  format,
  startOfDay,
  endOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
  setMonth,
  setYear as dfSetYear,
} from 'date-fns'
import './DateRange.scss'
import { BOOKING_LOCATION_FILTERS } from '../../utils/locations'

type Option = {
  label: string
  value: string
  from: any
  to: any
}

type Props = {
  onChange: (from: string, to: string, location: string) => void
}

export const DateRangeSelector: React.FC<Props> = ({ onChange }) => {
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [selectedValue, setSelectedValue] = useState<string>('today')

  const [customFrom, setCustomFrom] = useState<string>('')
  const [customTo, setCustomTo] = useState<string>('')
  const [selectedDates, setSelectedDates] = useState({
    from: format(today, 'yyyy-MM-dd'),
    to: format(today, 'yyyy-MM-dd'),
  })

  const predefinedOptions: Option[] = [
    {
      label: 'Today',
      value: 'today',
      from: startOfDay(today),
      to: endOfDay(today),
    },
    {
      label: 'Yesterday',
      value: 'yesterday',
      from: startOfDay(subDays(today, 1)),
      to: endOfDay(subDays(today, 1)),
    },
    {
      label: 'This Week',
      value: 'this_week',
      from: startOfWeek(today, { weekStartsOn: 1 }),
      to: endOfWeek(today, { weekStartsOn: 1 }),
    },
    {
      label: 'Last Week',
      value: 'last_week',
      from: startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
      to: endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
    },
    {
      label: '2 Weeks Ago',
      value: 'two_weeks_ago',
      from: startOfWeek(subWeeks(today, 2), { weekStartsOn: 1 }),
      to: endOfWeek(subWeeks(today, 2), { weekStartsOn: 1 }),
    },
    {
      label: 'This Month',
      value: 'this_month',
      from: startOfMonth(today),
      to: endOfMonth(today),
    },
    {
      label: 'Last Month',
      value: 'last_month',
      from: startOfMonth(subMonths(today, 1)),
      to: endOfMonth(subMonths(today, 1)),
    },
    {
      label: '2 Months Ago',
      value: 'two_months_ago',
      from: startOfMonth(subMonths(today, 2)),
      to: endOfMonth(subMonths(today, 2)),
    },
    {
      label: 'All Time',
      value: 'all_time',
      from: '',
      to: '',
    },
    {
      label: 'Custom Range',
      value: 'custom_range',
      from: '',
      to: '',
    },
  ]

  const months = Array.from({ length: 12 }).map((_, i) => {
    const date = setMonth(dfSetYear(new Date(), year), i)
    return {
      label: format(date, 'MMMM'),
      value: `month_${i}`,
      from: startOfMonth(date),
      to: endOfMonth(date),
    }
  })

  const allOptions = [
    { group: 'Quick Ranges', options: predefinedOptions },
    { group: `Months of ${year}`, options: months },
  ]

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setSelectedValue(value)

    if (value === 'all_time') {
      onChange('', '', location)
      setSelectedDates({
        from: '',
        to: '',
      })
    } else if (value !== 'custom_range') {
      const selected = [...predefinedOptions, ...months].find(
        (o) => o.value === value,
      )
      if (selected) {
        setSelectedDates({
          from: selected.from,
          to: selected.to,
        })
        onChange(
          format(selected.from, 'yyyy-MM-dd'),
          format(selected.to, 'yyyy-MM-dd'),
          location,
        )
      }
    }
  }

  const [location, setLocation] = useState('')
  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setLocation(value)
    if (
      selectedValue === 'all_time' ||
      (!selectedDates.from &&
        !selectedDates.to &&
        selectedValue !== 'custom_range')
    ) {
      onChange('', '', value)
    } else if (selectedValue === 'custom_range') {
      if (customFrom && customTo) {
        onChange(customFrom, customTo, value)
      }
    } else {
      onChange(
        format(new Date(selectedDates.from), 'yyyy-MM-dd'),
        format(new Date(selectedDates.to), 'yyyy-MM-dd'),
        value,
      )
    }
  }

  useEffect(() => {
    if (
      selectedValue === 'custom_range' &&
      customFrom &&
      customTo &&
      new Date(customFrom) <= new Date(customTo)
    ) {
      onChange(customFrom, customTo, location)
    }
  }, [customFrom, customTo, selectedValue])

  return (
    <div className='flex date-range'>
      <div className='flex one items-center'>
        <p>Select Date Range</p>
        <select onChange={handleSelectChange} value={selectedValue}>
          {allOptions.map((group) => (
            <optgroup key={group.group} label={group.group}>
              {group.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {selectedValue === 'custom_range' && (
        <div className='flex items-center custom-dates'>
          <label>
            From:{' '}
            <input
              type='date'
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
          </label>
          <label>
            To:{' '}
            <input
              type='date'
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              min={customFrom}
            />
          </label>
        </div>
      )}

      <div className='flex one items-center'>
        <p>Year:</p>
        <input
          type='number'
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
        />
      </div>

      <div className='flex items-center'>
        <p>Location</p>
        <select onChange={handleLocationChange} value={location}>
          {BOOKING_LOCATION_FILTERS.map((loc) => (
            <option key={loc.value} value={loc.value}>
              {loc.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
